import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/config";
import { Button } from "../components/ui/button";
import {
  Loader2, CheckCircle2, ArrowLeft, MapPin, Calendar, Phone, Mail,
  CreditCard, Receipt as ReceiptIcon, Printer, Download,
} from "lucide-react";

export default function Receipt() {
  const { txId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/payments/${txId}/receipt`);
        setData(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Receipt not found");
      } finally { setLoading(false); }
    })();
  }, [txId]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#047857]" /></div>;
  }
  if (error || !data) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-extrabold tracking-tighter font-[Outfit] text-gray-900">{error || "Receipt not found"}</h1>
        <Button onClick={() => navigate("/dashboard")} className="mt-6 rounded-full">Go to dashboard</Button>
      </div>
    );
  }

  const amount = data.amount || 0;
  const paidAt = data.paid_at ? new Date(data.paid_at).toLocaleString() : "—";
  const merchant = data.merchant || {};
  const customer = data.customer || {};
  const booking = data.booking || {};
  const quote = data.quote || {};
  const kind = data.kind || "payment";

  return (
    <div className="max-w-2xl mx-auto px-4 py-8" data-testid="receipt-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-gray-500 hover:text-[#047857] mb-4">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </button>

      <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Hero */}
        <div className="bg-gradient-to-br from-[#064E3B] to-[#0B4029] text-white px-6 py-7">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-emerald-200">{merchant.name || "JDSN Lawn Care"} · Receipt</div>
              <div className="mt-1 text-3xl font-extrabold tracking-tighter font-[Outfit]">
                ${amount.toFixed(2)}
              </div>
              <div className="mt-1 text-[12px] text-emerald-100/85">Paid {paidAt}</div>
            </div>
            <div className="bg-emerald-500/20 border border-emerald-300/30 rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-extrabold text-emerald-100 flex items-center gap-1.5" data-testid="receipt-status">
              <CheckCircle2 className="w-3.5 h-3.5" /> Paid
            </div>
          </div>
          <div className="mt-4 text-[11px] text-emerald-100/70">
            Receipt ID: <span className="font-mono tracking-wide">{data.tx_id}</span>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Charge breakdown */}
          <Section title="Charge details">
            <Row label="Type" value={data.payment_type === "deposit" ? "50% deposit" : data.payment_type === "full" ? "Paid in full" : (kind === "tip" ? "Tip" : "Payment")} />
            <Row label="Amount" value={`$${amount.toFixed(2)} ${String(data.currency || "usd").toUpperCase()}`} />
            <Row label="Status" value="Paid" valueClassName="text-emerald-700 font-extrabold" />
            <Row label="Date" value={paidAt} />
          </Section>

          {/* Booking */}
          {booking?.booking_id && (
            <Section title="Service">
              <Row label="Service" value={booking.service_name || quote.title || "—"} />
              {booking.preferred_date && (
                <Row label="Appointment" value={`${booking.preferred_date} · ${booking.preferred_time || ""}`} icon={Calendar} />
              )}
              {booking.address && <Row label="Address" value={booking.address} icon={MapPin} />}
              {booking.amount_due > 0 && (
                <Row label="Balance on completion" value={`$${(booking.amount_due || 0).toFixed(2)}`} />
              )}
            </Section>
          )}

          {/* Customer */}
          <Section title="Customer">
            <Row label="Name" value={customer.name || "—"} />
            {customer.email && <Row label="Email" value={customer.email} icon={Mail} />}
            {customer.phone && <Row label="Phone" value={customer.phone} icon={Phone} />}
            {customer.address && <Row label="Service address" value={customer.address} icon={MapPin} />}
          </Section>

          {/* Merchant */}
          <Section title="Paid to">
            <Row label="Business" value={merchant.name || "JDSN Lawn Care"} />
            <Row label="Location" value={merchant.address || "Jacksonville, FL"} icon={MapPin} />
            {merchant.phone && <Row label="Phone" value={merchant.phone} icon={Phone} />}
          </Section>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              onClick={() => window.print()}
              variant="outline"
              className="rounded-full font-bold"
              data-testid="receipt-print"
            >
              <Printer className="w-4 h-4 mr-1.5" /> Print / Save as PDF
            </Button>
            {booking?.booking_id && (
              <Button
                onClick={() => navigate(`/dashboard?booking=${booking.booking_id}`)}
                className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold"
                data-testid="receipt-view-booking"
              >
                <ReceiptIcon className="w-4 h-4 mr-1.5" /> View booking
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 text-center text-[11px] text-gray-500">
        Thanks for choosing JDSN Lawn Care · Jacksonville, FL
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-gray-500 mb-2">{title}</div>
      <div className="bg-[#F9FAFB] border border-gray-200 rounded-2xl divide-y divide-gray-100">{children}</div>
    </div>
  );
}

function Row({ label, value, icon: Icon, valueClassName = "" }) {
  return (
    <div className="px-4 py-2.5 flex items-start justify-between gap-3">
      <div className="text-[12px] uppercase tracking-[0.14em] font-bold text-gray-500 flex items-center gap-1">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className={`text-sm font-bold text-gray-900 text-right ${valueClassName}`}>{value}</div>
    </div>
  );
}
